/**
 * "The server just made a subscription-access decision the client should
 * reconcile with."
 *
 * The backend auth gate (SubscriptionAccessService) is the ONLY authority on
 * whether an organization has access. The client caches `/subscriptions/current`
 * for rendering, and that cache can drift — a trial lapsing mid-session, a
 * payment confirmed by webhook, a cancellation from another tab. When a guarded
 * API call is answered with 403 SUBSCRIPTION_EXPIRED (or 503
 * SUBSCRIPTION_STATE_UNAVAILABLE), the API client emits this signal and the
 * app refetches the subscription, so the gate UI converges on the server's
 * answer instead of silently contradicting it.
 *
 * A module-level event target rather than React context: the API client is not
 * a React component and must not import the query client.
 */

export interface SubscriptionAccessSignal {
  code: 'SUBSCRIPTION_EXPIRED' | 'SUBSCRIPTION_STATE_UNAVAILABLE' | string;
  state?: string;
}

type Listener = (signal: SubscriptionAccessSignal) => void;

const listeners = new Set<Listener>();

export function emitSubscriptionAccessSignal(signal: SubscriptionAccessSignal): void {
  for (const l of listeners) {
    try {
      l(signal);
    } catch {
      /* a listener failure must never break the request that triggered it */
    }
  }
}

export function onSubscriptionAccessSignal(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
